import React from 'react';

import Lottie from 'lottie-react';
import useFetch from 'react-fetch-hook';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import theme from 'styles/theme';

import CtaButton from 'components/CtaButton/CtaButton';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import { PromoBannerI } from 'interfaces/content-stack/modular-blocks/promoBanners.types';

import { autoOptimizeImage, findVideoExtension } from 'utils/functions';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const MastheadSection = styled.section<{ $backgroundUrl?: string }>`
  display: flex;
  align-items: center;
  height: 93.75vh;
  background-color: ${(props) => props.theme.colors.black};
  color: ${(props) => props.theme.colors.white};
  position: relative;
  overflow: hidden;
  font-family: ${(props) => props.theme.fonts.default};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;

  ${({ $backgroundUrl }) =>
    $backgroundUrl &&
    `
  background-image: url(${$backgroundUrl});
  background-position: 50%;
  background-repeat: repeat-y;
  background-size: cover;
  background-attachment: fixed;`}

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-top: 10rem;
    height: auto;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-top: 7rem;
  }
`;

const OuterContainer = styled.div`
  width: 100%;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 5rem;
  padding-right: 5rem;
  position: relative;
  z-index: 9;

  @media screen and (min-width: 1440px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media screen and (min-width: 1920px) {
    max-width: 1340px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-left: 3rem;
    padding-right: 3rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const InnerContainer = styled.div`
  padding-top: 12.5rem;
  padding-bottom: 10rem;

  @media screen and (min-width: 1440px) {
    padding-bottom: 14rem;
  }

  @media screen and (min-width: 1920px) {
    padding-top: 8.5rem;
    padding-bottom: 17rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-top: 0;
    padding-bottom: 4.5rem;
  }
`;

const MastheadGrid = styled.div`
  gap: 2rem;
  grid-template-rows: auto;
  grid-template-columns: 1fr 1fr;
  grid-auto-columns: 1fr;
  display: grid;

  @media screen and (min-width: 1920px) {
    grid-column-gap: 6rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const TextElementsContainer = styled.div`
  grid-row-gap: 1.25rem;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  display: flex;
  align-self: center;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    align-items: stretch;
  }
`;

const ButtonsContainer = styled.div`
  grid-area: span 1 / span 2 / span 1 / span 2;
  gap: 1rem;
  flex-direction: row;
  align-items: center;
  display: flex;
  animation: fadeIn 2s;
  margin-top: 1.2rem;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-column: span 1 / span 1;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    flex-direction: column;
    grid-row-gap: 1.5rem;
  }
`;

const OverlayPlaceholder = styled.div`
  grid-area: span 1 / span 1 / span 1 / span 1;
`;

const OverlayImageContainer = styled.div`
  grid-area: span 1 / span 1 / span 1 / span 1;

  z-index: 4;
  height: 46vh;
  transform: none;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: 1920px) {
    height: 62vh;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    order: -1;
    height: 25rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    height: auto;
  }
`;

const LottieContainer = styled(Lottie)`
  grid-area: span 1 / span 1 / span 1 / span 1;

  z-index: 4;
  height: 46vh;
  transform: none;

  @media screen and (min-width: 1920px) {
    height: 62vh;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    order: -1;
    height: 25rem;
    transform: scale(1.2);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    height: auto;
    transform: scale(1.1);
  }
`;

const OverlayImage = styled.img`
  max-height: 100%;
  max-width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    max-width: 80.5vw;
    max-height: 80.5vw;
  }
`;

const BackgroundContainer = styled.div`
  z-index: 1;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
`;

const BackgroundFilter = styled.div`
  will-change: opacity;
  opacity: 0.28309;
  z-index: 2;
  width: 100%;
  height: 100%;
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
  animation: fadeOut 3s;

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0.28309;
    }
  }
`;

const HeaderOverlay = styled.div`
  z-index: 3;
  width: 100%;
  mix-blend-mode: multiply;
  background-image: linear-gradient(to right, #1d191b 40%, rgba(27, 27, 27, 0));
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    background-image: linear-gradient(to top, rgba(29, 25, 27, 0.8) 12%, rgba(27, 27, 27, 0.3));
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    background-image: linear-gradient(to top, rgba(29, 25, 27, 0.9) 12%, rgba(27, 27, 27, 0.5));
  }
`;

const MastheadHeader = styled.div`
  h1 {
    transform: translate3d(0px, 0%, 0px);
    transform-style: preserve-3d;
    font-size: 4rem;
    font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 400;
    line-height: 1.4;
    animation: slideIn 1s;

    @keyframes slideIn {
      0% {
        transform: translate3d(0px, 100%, 0px);
      }
      100% {
        transform: translate3d(0px, 0%, 0px);
      }
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
      font-size: 3rem;
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.xs}) {
      font-size: 2.25rem;
    }
  }
`;

const MastheadSubheader = styled.div`
  opacity: 1;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 500;
  animation: fadeIn 2s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  sup {
    top: -0.5em;
    vertical-align: baseline;
    font-size: 75%;
    line-height: 0;
    position: relative;
  }
`;

const MastheadVideo = styled.video`
  transform: scale3d(1, 1, 1);
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -100;
  background-position: 50%;
  background-size: cover;
  margin: auto;
  position: absolute;
  top: -100%;
  bottom: -100%;
  left: -100%;
  right: -100%;
  animation: zoomOut 2s;

  @keyframes zoomOut {
    0% {
      transform: scale3d(1.2, 1.2, 1.2);
    }
    100% {
      transform: scale3d(1, 1, 1);
    }
  }
`;

const HeaderMask = styled.div`
  overflow: hidden;
`;

export interface PromotionBannersProps {
  data: PromoBannerI;
  className: string;
}

const PromotionBanners: React.FC<PromotionBannersProps> = ({ data, className }) => {
  const blockReference = data?.blockReference ? data.blockReference[0] : {};
  const video = blockReference?.backgroundVideo?.videoAsset;
  const videoExt = findVideoExtension(video) || 'mp4';
  const backgroundUrl = autoOptimizeImage(blockReference?.backgroundImage?.imageAsset?.url ?? '');
  const overlayImageUrl = autoOptimizeImage(blockReference?.overlayImage?.imageAsset?.url);
  const { isLoading, data: animationData } = useFetch(blockReference?.overlayImage?.animatedAsset?.url ?? '');

  return (
    <>
      <Helmet>
        <link rel='preload' fetchPriority='high' as='image' href={backgroundUrl} />
      </Helmet>

      <MastheadSection data-elastic-exclude className={className} $backgroundUrl={backgroundUrl}>
        <OuterContainer>
          <InnerContainer>
            <MastheadGrid>
              <TextElementsContainer>
                <HeaderMask>
                  <MastheadHeader>
                    <InnerHtml richText={blockReference.header} />
                  </MastheadHeader>
                </HeaderMask>
                {blockReference?.subheader && (
                  <MastheadSubheader>
                    <InnerHtml richText={blockReference.subheader} fontWeight={'400'} />
                  </MastheadSubheader>
                )}
                <ButtonsContainer>
                  {blockReference.buttonCta?.map((buttonCta, index) => {
                    const { link: url, openInNewWindow } = getModularBlockLinkData(buttonCta.buttonLink);

                    return (
                      <CtaButton
                        key={index}
                        to={url}
                        buttonType={buttonCta.buttonType}
                        text={buttonCta.buttonText}
                        openInNewWindow={openInNewWindow}
                        textColor={theme.colors.white}
                        faint
                        banner
                        dataLayerInfo={blockReference.header}
                      />
                    );
                  })}
                </ButtonsContainer>
              </TextElementsContainer>
              {!isLoading && animationData ? (
                <LottieContainer animationData={animationData} />
              ) : overlayImageUrl ? (
                <OverlayImageContainer>
                  <OverlayImage
                    src={overlayImageUrl}
                    alt={blockReference?.overlayImage?.altImageText ?? 'Header overlay image'}
                  />
                </OverlayImageContainer>
              ) : (
                <OverlayPlaceholder />
              )}
            </MastheadGrid>
          </InnerContainer>
        </OuterContainer>
        <BackgroundContainer>
          {video && (
            <MastheadVideo
              autoPlay={true}
              loop={false}
              muted={true}
              playsInline={true}
              poster={backgroundUrl ?? 'Poster'}
            >
              <source src={video?.url} type={`video/${videoExt}`} />
            </MastheadVideo>
          )}
        </BackgroundContainer>
        <BackgroundFilter />
        <HeaderOverlay />
      </MastheadSection>
    </>
  );
};

export default PromotionBanners;
